import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle, ChevronDown, ChevronRight, ExternalLink, Eye, Mail,
  MessageSquare, RefreshCw, Search, Users, Wallet2, X,
} from "lucide-react";
import {
  fetchSentActivity, fetchSentGroups, fetchSentMessage,
} from "../api/communicationsApi";
// Every timestamp in the CRM is shown as Melbourne wall-clock time, whatever
// the viewer's own machine is set to (src/utils/melbourneTime.js).
import { formatMelbourneDateTime } from "../../../utils/melbourneTime";

/**
 * "Sent" - what actually went out, to whom, and what credits are left.
 *
 * Christian Gravias (Monday 12887883570, 2026-09-01): "Can we please have a
 * section where we can see how many texts/emails have gone out and who has
 * received the texts and like an alert if we run out of credits".
 *
 * Reads the backend's CommunicationLog roll-up rather than campaign counters,
 * which matters: a Find Buyers blast is not a campaign, so campaign analytics
 * never showed the sends this question is mostly about. Everything the CRM sends
 * writes a log row - campaigns, Find Buyers, buyer alerts, portal invites - so
 * everything is counted here.
 *
 * GROUPED BY SEND (Christian Gravias, Monday 13057482228, 2026-09-16): "Please
 * break it down as per campaign rather then individual texts. Its taking up to
 * much of the page and I want to view it based on groups with the option to
 * click and view individual. I want to also be able to read the message that the
 * agents sent at the campaign level."
 *
 * ROUND 2 (same ticket, 2026-09-17): "Please show a link to click to view the
 * actual email that went out, not just text. Would like to see photos, links,
 * layout etc. For the who received it section please also add clicked &
 * unsubscribed to track whos clicking or unsubscribing individually. Please
 * also make the colour of the email logo purple and the colour of the texts
 * gone out blue to differentiate easier."
 *
 * So the table now opens as one row per SEND. A 2,000-recipient text blast is
 * one line, not 2,000, and it opens up to the individual messages underneath.
 * Each send also carries the message it went out with, because the per-person
 * rows only ever held one personalised copy each. "Every message" switches back
 * to the flat list, which is still the right shape when you are searching for
 * one buyer rather than reviewing what the office sent.
 */

// Christian Gravias, Monday 13058812936, 17 Sep 2026: "Please also add a 1 day
// tracker in the communications hub." The shortest window was 7 days, so there
// was no way to see what had gone out today. The backend already accepts any
// window from 1 to 366 days, so this is purely the missing button.
const PERIODS = [
  { days: 1, label: "24 hours" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
];

const STATUS_STYLES = {
  sent: "bg-green-100 text-green-700",
  delivered: "bg-green-100 text-green-700",
  failed: "bg-red-100 text-red-700",
  skipped: "bg-gray-100 text-gray-500",
  unsubscribed: "bg-amber-100 text-amber-700",
};

// Email is purple, text is blue, everywhere on this screen - the icons, the
// counters and the row chips (Christian Gravias, Monday 13057482228, 17 Sep
// 2026: "make the colour of the email logo purple and the colour of the texts
// gone out blue to differentiate easier"). Both were grey before, so the only
// thing separating an email send from a text send at a glance was the shape of
// a small icon.
const EMAIL_COLOR = "text-purple-600";
const SMS_COLOR = "text-blue-600";

function StatCard({ Icon, label, value, hint, tone = "gray" }) {
  const tones = {
    gray: "text-gray-400",
    email: EMAIL_COLOR,
    sms: SMS_COLOR,
    amber: "text-amber-500",
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${tones[tone] || tones.gray}`} />
        <p className="text-xs font-semibold text-gray-500">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      {hint && <p className="text-[11px] text-gray-400 mt-0.5">{hint}</p>}
    </div>
  );
}

function ChannelIcon({ channel, className = "w-3.5 h-3.5 shrink-0" }) {
  const colour = channel === "sms" ? SMS_COLOR : EMAIL_COLOR;
  return channel === "sms"
    ? <MessageSquare className={`${className} ${colour}`} title="Text" />
    : <Mail className={`${className} ${colour}`} title="Email" />;
}



/**
 * What one person did with one message, small enough for a wide table.
 *
 * The drill-down under a send has the room for three dated columns; the flat
 * list does not, but it is the view an agent lands on when they search for one
 * buyer by name - and "did they click, did they unsubscribe" is exactly what
 * they are looking for (Monday 13057482228, 17 Sep 2026).
 */
function Engagement({ row }) {
  const marks = [
    row.opened_at && { label: "Opened", at: row.opened_at, cls: "bg-gray-100 text-gray-600" },
    row.clicked_at && { label: "Clicked", at: row.clicked_at, cls: "bg-green-100 text-green-700" },
    row.unsubscribed_at && { label: "Unsubscribed", at: row.unsubscribed_at, cls: "bg-amber-100 text-amber-700" },
  ].filter(Boolean);
  if (!marks.length) return <span className="text-gray-300">-</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {marks.map((m) => (
        <span
          key={m.label}
          title={`${m.label} ${formatMelbourneDateTime(m.at)}`}
          className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${m.cls}`}
        >
          {m.label}
        </span>
      ))}
    </span>
  );
}

/**
 * The email exactly as it landed in somebody's inbox.
 *
 * Christian Gravias (Monday 13057482228, 17 Sep 2026): "Please show a link to
 * click to view the actual email that went out, not just text. Would like to
 * see photos, links, layout etc."
 *
 * The plain-text block below is still the right thing for a text message and
 * for a quick read, but an email is a designed thing - header, logo, property
 * photos, buttons - and none of that survives being flattened to text. The
 * backend rebuilds the sent HTML through the same builder the send itself used,
 * personalised for one real recipient, with the unsubscribe link deadened; this
 * renders it in a sandboxed iframe so nothing in an old email can run here.
 */
function SentEmailModal({ group, query, onClose }) {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchSentMessage(group.key, query)
      .then((d) => active && setMessage(d))
      .catch(() => active && setFailed(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [group.key, query]);

  // Opening the same HTML in its own tab, for reading a long email at full
  // height. A blob URL keeps it local - the email is never re-fetched or posted
  // anywhere to be displayed.
  const openInTab = () => {
    const blob = new Blob([message?.html || ""], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const html = (message?.html || "").trim();

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="The email that went out"
    >
      <div className="bg-white rounded-xl shadow-2xl w-[900px] max-w-full max-h-[90vh] flex flex-col">
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 truncate">
              {message?.subject || group.subject || group.name}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {group.name}
              {message?.sent_by ? ` · sent by ${message.sent_by}` : ""}
              {group.last_at ? ` · ${formatMelbourneDateTime(group.last_at)}` : ""}
              {/* Named on purpose: this is ONE person's copy. "Hi Joanne" in the
                  body would otherwise read as the agent having typed a name. */}
              {message?.rendered_for ? ` · shown as ${message.rendered_for} received it` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {html && (
              <button
                type="button"
                onClick={openInTab}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open in a new tab
              </button>
            )}
            <button onClick={onClose} aria-label="Close" className="text-gray-500">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto px-5 py-4 bg-gray-50">
          {loading ? (
            <p className="text-sm text-gray-400">Loading the email…</p>
          ) : failed ? (
            <p className="text-sm text-gray-500">
              The email could not be loaded. It may have been sent by another agent.
            </p>
          ) : html ? (
            <iframe
              title="The email that went out"
              sandbox=""
              srcDoc={html}
              className="w-full h-[62vh] border border-gray-200 rounded-lg bg-white"
            />
          ) : (
            /* Automated emails - a buyer alert, an enquiry auto-reply, a
               digest, a portal invite - now keep a copy of what went out, so
               this is only reached by sends made before 21 Sep 2026, the day
               snapshots reached production (the code was written 19 Sep, which
               is not the date that matters to anyone reading this). Nothing
               can be recovered for those: the log row kept the subject line and
               nothing else, so saying so is better than a blank frame. */
            <p className="text-sm text-gray-600">
              This one went out before we started keeping a copy of automated emails,
              so there is no layout to show for it. Anything sent from 21 September 2026
              can be read in full here. The text of this one is on the row below.
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100">
          <p className="text-[11px] text-gray-400">
            This is the email as it was delivered, with one recipient's details filled in.
            The unsubscribe link in it does nothing here.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The individual messages inside one send, loaded when the row is opened.
 *
 * Deliberately lazy: the whole point of grouping is that a blast's thousands of
 * rows are not on the page until someone asks for that one send.
 */
function GroupDetail({ group, query }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchSentActivity({ ...query, group_key: group.key, page: 1, page_size: 100 })
      .then((d) => {
        if (!active) return;
        setRows(d?.rows || []);
        setCount(d?.count || 0);
      })
      .catch(() => active && setRows([]))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [group.key, query]);

  const message = (group.message_text || "").trim();

  return (
    <div className="bg-gray-50 border-t border-gray-100 px-4 py-3 space-y-3">
      {/* What the agent actually wrote. Shown with the merge tags still in it,
          because that IS the message - each person got their own name put in. */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">
            Message sent
          </p>
          {/* Text messages have no layout to look at - what is written below IS
              what the phone showed - so the link is an email one only. */}
          {group.channel !== "sms" && (
            <button
              type="button"
              onClick={() => setViewing(true)}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-purple-700 hover:text-purple-900 hover:underline"
            >
              <Eye className="w-3.5 h-3.5" /> View the email that went out
            </button>
          )}
        </div>
        {message ? (
          <pre className="whitespace-pre-wrap text-xs text-gray-700 bg-white border border-gray-200 rounded-lg p-3 max-h-48 overflow-auto font-sans">
            {message}
          </pre>
        ) : (
          <p className="text-xs text-gray-400">
            No message was stored for this send.
          </p>
        )}
      </div>

      <div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1">
          Who received it
          {!loading && count > rows.length && (
            <span className="font-normal normal-case tracking-normal text-gray-400">
              {" "}- first {rows.length.toLocaleString()} of {count.toLocaleString()}
            </span>
          )}
        </p>
        {loading ? (
          <p className="text-xs text-gray-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-gray-400">Nothing to show.</p>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="max-h-72 overflow-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 text-left text-gray-500 sticky top-0">
                  <tr>
                    <th className="px-3 py-1.5 font-semibold">When</th>
                    <th className="px-3 py-1.5 font-semibold">To</th>
                    <th className="px-3 py-1.5 font-semibold">Opened</th>
                    {/* Who clicked and who unsubscribed, person by person
                        (Monday 13057482228, 17 Sep 2026). The send-level row
                        above has always had the counts; these are the names
                        behind them. */}
                    <th className="px-3 py-1.5 font-semibold">Clicked</th>
                    <th className="px-3 py-1.5 font-semibold">Unsubscribed</th>
                    <th className="px-3 py-1.5 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">
                        {formatMelbourneDateTime(row.occurred_at)}
                      </td>
                      <td className="px-3 py-1.5">
                        {row.name && <span className="text-gray-800">{row.name} </span>}
                        <span className="text-gray-500">{row.to}</span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-500 whitespace-nowrap">
                        {row.opened_at ? formatMelbourneDateTime(row.opened_at) : "-"}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {row.clicked_at
                          ? <span className="text-green-700 font-semibold">
                              {formatMelbourneDateTime(row.clicked_at)}
                            </span>
                          : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-3 py-1.5 whitespace-nowrap">
                        {row.unsubscribed_at
                          ? <span className="text-amber-700 font-semibold">
                              {formatMelbourneDateTime(row.unsubscribed_at)}
                            </span>
                          : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLES[row.status] || "bg-gray-100 text-gray-600"}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {viewing && (
        <SentEmailModal group={group} query={query} onClose={() => setViewing(false)} />
      )}
    </div>
  );
}

export default function SentActivityTab() {
  const [days, setDays] = useState(30);
  const [channel, setChannel] = useState("");
  const [scope, setScope] = useState("");     // "" = let the backend decide
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");   // debounced copy of q
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Grouped is the default: the flat list of every individual text was the
  // complaint (Monday 13057482228).
  const [grouped, setGrouped] = useState(true);
  const [openKey, setOpenKey] = useState(null);

  // Debounced, so typing a buyer's name is one request rather than one a letter.
  useEffect(() => {
    const t = setTimeout(() => { setSearch(q.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // The filters, without the paging - this is also what a drill-down re-sends so
  // an opened send shows the same window and channel as the row above it.
  const query = React.useMemo(() => ({
    days,
    ...(channel ? { channel } : {}),
    ...(scope ? { scope } : {}),
    ...(search ? { q: search } : {}),
  }), [days, channel, scope, search]);

  const load = useCallback(() => {
    setLoading(true);
    const fetcher = grouped ? fetchSentGroups : fetchSentActivity;
    fetcher({ ...query, page, page_size: 50 })
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [query, page, grouped]);

  useEffect(() => { load(); }, [load]);

  // Switching view or filters closes whatever was open - the key it referred to
  // may not even be on the new page.
  useEffect(() => { setOpenKey(null); }, [grouped, query, page]);

  const totals = data?.totals || {};
  const email = totals.email || {};
  const sms = totals.sms || {};
  const credits = data?.credits || {};
  const rows = data?.rows || [];
  const groups = data?.groups || [];
  const canSeeAgency = !!data?.can_see_agency;
  const activeScope = data?.scope || "mine";

  const pill = (active) =>
    `px-3 py-1.5 rounded-lg text-xs font-bold transition ${
      active ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500 hover:text-gray-700"
    }`;

  return (
    <div className="space-y-4">
      {/* Out of credits is the one thing on this screen that needs saying before
          anything else - a blast that runs out halfway leaves the last buyers on
          the list with nothing, silently. */}
      {credits.credits !== null && credits.credits !== undefined && credits.low && (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>SMS credits are running low</strong> - {credits.credits.toLocaleString()} left
            (we start warning under {(credits.threshold || 0).toLocaleString()}). Texts stop sending
            at zero, so top up the Mobile Message account before the next big send. The admins are
            emailed about this automatically too.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {PERIODS.map((p) => (
            <button key={p.days} onClick={() => { setDays(p.days); setPage(1); }}
              className={pill(days === p.days)}>
              {p.label}
            </button>
          ))}
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[["", "Both"], ["email", "Email"], ["sms", "Text"]].map(([id, label]) => (
            <button key={id || "both"} onClick={() => { setChannel(id); setPage(1); }}
              className={pill(channel === id)}>
              {label}
            </button>
          ))}
        </div>

        {canSeeAgency && (
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            {[["agency", "Whole agency"], ["mine", "Just mine"]].map(([id, label]) => (
              <button key={id} onClick={() => { setScope(id); setPage(1); }}
                className={pill(activeScope === id)}>
                {label}
              </button>
            ))}
          </div>
        )}

        {/* One send per line, or one message per line. */}
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          {[[true, "By campaign"], [false, "Every message"]].map(([id, label]) => (
            <button key={String(id)} onClick={() => { setGrouped(id); setPage(1); }}
              className={pill(grouped === id)}>
              {label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a name, email, mobile or subject"
            aria-label="Search sent messages"
            className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm"
          />
        </div>

        <button type="button" onClick={load}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 hover:bg-gray-50">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard
          Icon={Mail} tone="email" label="Emails gone out"
          value={(email.went_out || 0).toLocaleString()}
          hint={`${(email.failed || 0).toLocaleString()} failed · ${(email.skipped || 0).toLocaleString()} skipped (opted out / no address)`}
        />
        <StatCard
          Icon={MessageSquare} tone="sms" label="Texts gone out"
          value={(sms.went_out || 0).toLocaleString()}
          hint={`${(sms.failed || 0).toLocaleString()} failed · ${(sms.skipped || 0).toLocaleString()} skipped (opted out / no number)`}
        />
        <StatCard
          Icon={Users} label="People reached"
          value={(data?.people || 0).toLocaleString()}
          hint="Distinct email addresses and mobiles, not message count"
        />
        <StatCard
          Icon={Wallet2} tone={credits.low ? "amber" : "gray"} label="SMS credits left"
          value={credits.credits === null || credits.credits === undefined
            ? "-"
            : credits.credits.toLocaleString()}
          hint={credits.credits === null || credits.credits === undefined
            /* Never shown as 0: unknown and empty are different things, and a
               false "0 credits" would stop agents sending for no reason. */
            ? "Balance unavailable from the SMS provider"
            : credits.low ? "Running low - worth topping up" : "Live from the SMS account"}
        />
      </div>

      {/* Where the sends came from */}
      {!!data?.by_source?.length && (
        <div className="flex flex-wrap gap-2">
          {data.by_source.map((row) => (
            <span key={`${row.source}-${row.channel}`}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-xs text-gray-600">
              <ChannelIcon channel={row.channel} className="w-3 h-3" />
              {row.label}
              <span className="font-bold text-gray-800">{row.count.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}

      {/* The table: one row per send, or one row per message */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap">
          <p className="font-bold text-gray-800 text-sm">
            {grouped ? "What was sent" : "Who received it"}
          </p>
          <p className="text-xs text-gray-400">
            {grouped
              ? `${(data?.count || 0).toLocaleString()} send${data?.count === 1 ? "" : "s"}` +
                (data?.message_count
                  ? ` · ${data.message_count.toLocaleString()} message${data.message_count === 1 ? "" : "s"}`
                  : "")
              : `${(data?.count || 0).toLocaleString()} message${data?.count === 1 ? "" : "s"}`}
            {activeScope === "mine" && " you sent"}
          </p>
        </div>

        <div className="overflow-x-auto">
          {grouped ? (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-semibold w-8"></th>
                  <th className="px-4 py-2 font-semibold">Send</th>
                  <th className="px-4 py-2 font-semibold">Sent by</th>
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold text-right">Recipients</th>
                  <th className="px-4 py-2 font-semibold text-right">Opened</th>
                  <th className="px-4 py-2 font-semibold text-right">Clicked</th>
                  <th className="px-4 py-2 font-semibold text-right">Unsubscribed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groups.map((g) => {
                  const open = openKey === g.key;
                  return (
                    <React.Fragment key={g.key}>
                      <tr
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setOpenKey(open ? null : g.key)}
                      >
                        <td className="px-4 py-2 text-gray-400">
                          {open
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1.5">
                            <ChannelIcon channel={g.channel} />
                            <div className="min-w-0">
                              <p className="text-gray-800 font-semibold truncate max-w-[340px]" title={g.name}>
                                {g.name}
                              </p>
                              <p className="text-xs text-gray-400 truncate max-w-[340px]">
                                {g.source_label}
                                {g.subject ? ` · ${g.subject}` : ""}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {g.sent_by || <span className="text-gray-300">Automatic</span>}
                          {/* An automatic "Just listed" alert names the listing
                              agent here, not as its sender (Monday 13226450936). */}
                          {!g.sent_by && g.listing_agent && (
                            <span className="block text-[11px] text-gray-400">
                              Listing: {g.listing_agent}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                          {formatMelbourneDateTime(g.last_at)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                          {(g.went_out || 0).toLocaleString()}
                          {!!g.skipped && (
                            <span className="text-gray-400"> (+{g.skipped.toLocaleString()} skipped)</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                          {(g.opened || 0).toLocaleString()}{" "}
                          <span className="text-gray-400">({g.open_rate}%)</span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-700">
                          {(g.clicked || 0).toLocaleString()}{" "}
                          <span className="text-gray-400">({g.click_rate}%)</span>
                        </td>
                        <td className={`px-4 py-2 text-right tabular-nums ${g.unsubscribed ? "text-amber-700" : "text-gray-700"}`}>
                          {(g.unsubscribed || 0).toLocaleString()}{" "}
                          <span className="text-gray-400">({g.unsub_rate}%)</span>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={8} className="p-0">
                            <GroupDetail group={g} query={query} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
                {!groups.length && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">
                      {loading ? "Loading…" : "Nothing sent in this period."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2 font-semibold">When</th>
                  <th className="px-4 py-2 font-semibold">To</th>
                  <th className="px-4 py-2 font-semibold">Message</th>
                  <th className="px-4 py-2 font-semibold">From</th>
                  <th className="px-4 py-2 font-semibold">Sent by</th>
                  <th className="px-4 py-2 font-semibold">What they did</th>
                  <th className="px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">{formatMelbourneDateTime(row.occurred_at)}</td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-1.5">
                        <ChannelIcon channel={row.channel} />
                        <div className="min-w-0">
                          {row.name && <p className="text-gray-800 truncate">{row.name}</p>}
                          <p className="text-xs text-gray-500 truncate">{row.to}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600 max-w-[320px]">
                      <p className="truncate" title={row.subject}>{row.subject}</p>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {row.campaign_name || row.batch_name || row.source_label}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                      {row.sent_by || <span className="text-gray-300">Automatic</span>}
                    </td>
                    <td className="px-4 py-2">
                      <Engagement row={row} />
                    </td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${STATUS_STYLES[row.status] || "bg-gray-100 text-gray-600"}`}>
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-400">
                      {loading ? "Loading…" : "Nothing sent in this period."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {(page > 1 || data?.has_more) && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
            <button type="button" disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 disabled:opacity-40">
              Previous
            </button>
            <span className="text-xs text-gray-400">Page {page}</span>
            <button type="button" disabled={!data?.has_more || loading}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-bold text-gray-600 disabled:opacity-40">
              Next
            </button>
          </div>
        )}
      </div>

      <p className="text-[11px] text-gray-400">
        Counts every message the CRM sent - campaigns, Find Buyers blasts, buyer alerts and portal
        invites. "Skipped" is someone who opted out or had no address, and is never charged.
        {grouped && " Click a send to read the message and see everyone who received it."}
        {!canSeeAgency && " You are seeing your own sends."}
      </p>
    </div>
  );
}
